import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
// Eager: auth entry points + dashboard are load-critical and lightweight, so
// they stay in the main bundle. Everything heavy is lazy-loaded below.
import DashboardPage from "@/pages/DashboardPage";
import LoginPage from "@/components/LoginPage";
import RegisterPage from "@/components/RegisterPage";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
// ADO #574 — the Code Next moment, opened by a passed build on any surface.
import CodeNextWowHost from "@/components/codenext/CodeNextWowHost";
// The course is desktop-first: one dismissible notice on phones, signed-in pages.
import PhoneNotice from "@/components/PhoneNotice";
// ADO #575 — a crash shows a friendly page with Reload (never a blank one) and
// is reported, with uncaught errors and rejections, to the admin console.
import { AppErrorBoundary, RouteErrorBoundary } from "@/components/AppErrorBoundary";
import { installErrorReporter } from "@/services/errorReporter";
import { preloadLanguageConfigs } from "@/validation";
import * as githubService from "@/services/githubService";

// Route-based code splitting. The Visual Flow Builder + Solution page pull in
// Monaco and the whole flow-builder tree (the bulk of the JS weight); keeping
// them out of the main bundle means the dashboard/login load without paying for
// code they never use. Each lazy() import becomes its own on-demand chunk.
const LMSPage             = lazy(() => import("@/components/LMSPage"));
const FlowBuilderFullScreen = lazy(() => import("@/pages/FlowBuilderFullScreen"));
const VisualFlowBuilder   = lazy(() => import("@/pages/VisualFlowBuilder"));
const ProjectsPage        = lazy(() => import("@/pages/ProjectsPage"));
const SolutionPage        = lazy(() => import("@/pages/SolutionPage"));
const TopicAssessmentPage = lazy(() => import("@/pages/TopicAssessmentPage"));
const CourseOutlinePage   = lazy(() => import("@/pages/CourseOutlinePage"));
// ADO #510 — a capstone's board: one project, many user stories.
const CapstonePage        = lazy(() => import("@/pages/CapstonePage"));
const CapstoneStoryPage   = lazy(() => import("@/pages/CapstoneStoryPage"));
// ADO #512 — a SQL client over the student's own engine, outside the builder.
const SqlClientPage       = lazy(() => import("@/pages/SqlClientPage"));
const SheetClientPage       = lazy(() => import("@/pages/SheetClientPage"));
const ClientPage       = lazy(() => import("@/pages/ClientPage"));
const FinishedDatabasePage = lazy(() => import("@/pages/FinishedDatabasePage"));
// ADO #513 — a capstone share link. Public: the token is the credential.
const SharedCapstonePage  = lazy(() => import("@/pages/SharedCapstonePage"));
// The capstone projects list — the "organization" level of the Azure shape.
const CapstonesPage       = lazy(() => import("@/pages/CapstonesPage"));
const CartPage            = lazy(() => import("@/pages/CartPage"));
const CheckoutPage        = lazy(() => import("@/pages/CheckoutPage"));
const OrderHistoryPage    = lazy(() => import("@/pages/OrderHistoryPage"));
const ProfilePage         = lazy(() => import("@/pages/ProfilePage"));
const InvoicePage         = lazy(() => import("@/pages/InvoicePage"));
const StartPage           = lazy(() => import("@/pages/StartPage"));
const WelcomePage         = lazy(() => import("@/pages/WelcomePage"));
const ForgotPasswordPage  = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage   = lazy(() => import("@/pages/ResetPasswordPage"));
const NotFoundPage        = lazy(() => import("@/pages/NotFoundPage"));
// Go-live legal pages — public, drafts until REACT_APP_LEGAL_REVIEWED=true.
const PrivacyPage         = lazy(() => import("@/pages/legal/PrivacyPage"));
const TermsPage           = lazy(() => import("@/pages/legal/TermsPage"));
const RefundsPage         = lazy(() => import("@/pages/legal/RefundsPage"));

// Full-screen fallback shown while a route chunk is fetched.
function PageLoader() {
  return (
    <div className="h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
        <p className="text-slate-500 text-sm">Loading...</p>
      </div>
    </div>
  );
}

// ============================================
// GitHub OAuth callback — runs BEFORE React mounts
// ============================================
(() => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) return;
  // Prevent double-processing
  if (sessionStorage.getItem('f2c-github-processing')) return;
  sessionStorage.setItem('f2c-github-processing', '1');
  // Clean URL immediately
  const cleanUrl = new URL(window.location.href);
  cleanUrl.searchParams.delete('code');
  cleanUrl.searchParams.delete('state');
  window.history.replaceState({}, '', cleanUrl.toString());
  // Exchange code for token
  githubService.handleOAuthCallback(code)
    .then(() => {
      sessionStorage.removeItem('f2c-github-processing');
      const returnUrl = sessionStorage.getItem('f2c-github-return');
      sessionStorage.removeItem('f2c-github-return');
      if (returnUrl) {
        window.location.replace(returnUrl);
      }
    })
    .catch((err) => {
      sessionStorage.removeItem('f2c-github-processing');
      console.error('GitHub OAuth failed:', err);
    });
})();

// Protected Route Component - redirects to login if not authenticated
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  // DEV MODE: Allow bypass with ?dev=true query param (for testing)
  const urlParams = new URLSearchParams(window.location.search);
  const devMode = urlParams.get('dev') === 'true';
  
  if (devMode) {
    console.log('[DEV MODE] Bypassing auth check');
    return children;
  }
  
  // Show nothing while checking auth status
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Wrapper component to handle navigation
function AuthWrapper({ children, isLoggedIn, setIsLoggedIn }) {
  const navigate = useNavigate();

  const handleLogin = (userData) => {
    setIsLoggedIn(true);
    console.log('User logged in:', userData);
    navigate('/dashboard');
  };

  const handleRegister = (userData) => {
    setIsLoggedIn(true);
    console.log('User registered:', userData);
    navigate('/dashboard');
  };

  const handleSwitchToLogin = () => {
    navigate('/login');
  };

  const handleSwitchToRegister = () => {
    navigate('/register');
  };

  return children({ 
    handleLogin, 
    handleRegister, 
    handleSwitchToLogin, 
    handleSwitchToRegister,
    isLoggedIn 
  });
}

installErrorReporter();

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Preload language configs on app start for faster validation
  useEffect(() => {
    preloadLanguageConfigs(['c', 'cpp', 'python', 'java', 'javascript']).catch(console.error);
  }, []);

  // GitHub OAuth callback is handled at module level (above) before React mounts

  return (
    <div className="App">
      <AppErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AuthWrapper isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn}>
            {({ handleLogin, handleRegister, handleSwitchToLogin, handleSwitchToRegister, isLoggedIn }) => (
              <RouteErrorBoundary>
              <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Root redirects to login - public site handles marketing pages */}
                <Route path="/" element={<Navigate to="/login" replace />} />
                
                {/* Auth routes */}
                <Route 
                  path="/login" 
                  element={<LoginPage onSwitchToRegister={handleSwitchToRegister} />} 
                />
                <Route
                  path="/register"
                  element={<RegisterPage onSwitchToLogin={handleSwitchToLogin} />}
                />
                {/* Password reset — public; /reset-password consumes the recovery
                    token Supabase appends to the email link. */}
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                {/* Legal — public: readable before sign-up (the form links here). */}
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/refunds" element={<RefundsPage />} />
                
                {/* App routes - Protected */}
                <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                <Route path="/learn" element={<ProtectedRoute><LMSPage /></ProtectedRoute>} />
                <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
                <Route path="/flow-builder" element={<ProtectedRoute><FlowBuilderFullScreen /></ProtectedRoute>} />
                <Route path="/visual-flow-builder" element={<ProtectedRoute><VisualFlowBuilder /></ProtectedRoute>} />
                <Route path="/practice/:slug" element={<ProtectedRoute><SolutionPage /></ProtectedRoute>} />
                <Route path="/learn/topic/:topicId/assessment" element={<ProtectedRoute><TopicAssessmentPage /></ProtectedRoute>} />
                <Route path="/learn/course/:courseSlug/outline" element={<ProtectedRoute><CourseOutlinePage /></ProtectedRoute>} />
                <Route path="/capstones" element={<ProtectedRoute><CapstonesPage /></ProtectedRoute>} />
                <Route path="/capstone/:slug" element={<ProtectedRoute><CapstonePage /></ProtectedRoute>} />
                <Route path="/capstone/:slug/finished" element={<ProtectedRoute><FinishedDatabasePage /></ProtectedRoute>} />
                <Route path="/capstone/:slug/:section" element={<ProtectedRoute><CapstonePage /></ProtectedRoute>} />
                <Route path="/capstone/:slug/story/:storySlug" element={<ProtectedRoute><CapstoneStoryPage /></ProtectedRoute>} />
                <Route path="/sql/:projectId" element={<ProtectedRoute><SqlClientPage /></ProtectedRoute>} />
                <Route path="/sheet/:projectId" element={<ProtectedRoute><SheetClientPage /></ProtectedRoute>} />
                <Route path="/client/:projectId" element={<ProtectedRoute><ClientPage /></ProtectedRoute>} />
                <Route path="/capstone/:slug/s/:token" element={<SharedCapstonePage />} />
                
                {/* ADO #480 — "Try for Free" resolver: enroll into the free
                    Problem Solving course, then deep-link in. Handles its own
                    auth (unauth -> /register?next=/start), so it is NOT wrapped
                    in ProtectedRoute (which would bounce to /login). */}
                <Route path="/start" element={<StartPage />} />
                {/* A personal tour for an invited guest; public, not indexed. */}
                <Route path="/welcome/:code" element={<WelcomePage />} />

                {/* E-commerce routes */}
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute><OrderHistoryPage /></ProtectedRoute>} />
                <Route path="/orders/:orderId" element={<ProtectedRoute><InvoicePage /></ProtectedRoute>} />
                {/* Catch-all: an unknown/malformed URL previously rendered a
                    blank white screen. Show a proper Not Found page instead. */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              </Suspense>
              <PhoneNotice />
              </RouteErrorBoundary>
            )}
          </AuthWrapper>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-right" richColors />
      <CodeNextWowHost />
      </AppErrorBoundary>
    </div>
  );
}

export default App;
